import _JSON$stringify from 'babel-runtime/core-js/json/stringify';
/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import debounce from 'lodash/debounce';
import { StatusMessage } from '../status';
import { WatchableValue } from '../trackable_value';
import { RefCounted } from '../util/disposable';
import { responseJson } from '../util/http_request';
import { urlSafeParse, verifyObject } from '../util/json';
import { cancellableFetchSpecialOk, parseSpecialUrl } from '../util/special_protocol_request';
import { getCachedJson } from '../util/trackable';
/**
 * @file Implements a binding between a Trackable value and the URL hash state.
 */
/**
 * Encodes a fragment string robustly.
 */
export function encodeFragment(fragment) {
    return encodeURI(fragment).replace(/[!'()*;,]/g, function (c) {
        return '%' + c.charCodeAt(0).toString(16).toUpperCase();
    });
}
/**
 * An instance of this class manages a binding between a Trackable value and the URL hash state.
 * The binding is initialized in the constructor, and is removed when dispose is called.
 */
export class UrlHashBinding extends RefCounted {
    constructor(root, credentialsManager, options = {}) {
        super();
        this.root = root;
        this.credentialsManager = credentialsManager;
        /**
         * Most recent error parsing URL hash.
         */
        this.parseError = new WatchableValue(undefined);
        var _options$updateDelayM = options.updateDelayMilliseconds;
        const updateDelayMilliseconds = _options$updateDelayM === undefined ? 200 : _options$updateDelayM;
        var _options$defaultFragm = options.defaultFragment;
        const defaultFragment = _options$defaultFragm === undefined ? '{}' : _options$defaultFragm;

        this.registerEventListener(window, 'hashchange', () => this.updateFromUrlHash());
        const throttledSetUrlHash = debounce(() => this.setUrlHash(), updateDelayMilliseconds);
        this.registerDisposer(root.changed.add(throttledSetUrlHash));
        this.registerDisposer(() => throttledSetUrlHash.cancel());
        this.defaultFragment = defaultFragment;
    }
    /**
     * Sets the URL hash to match the current state.
     */
    setUrlHash() {
        const cacheState = getCachedJson(this.root);
        const generation = cacheState.generation;

        if (generation !== this.prevStateGeneration) {
            this.prevStateGeneration = cacheState.generation;
            let stateString = encodeFragment(_JSON$stringify(cacheState.value));
            if (stateString !== this.prevStateString) {
                this.prevStateString = stateString;
                if (decodeURIComponent(stateString) === '{}') {
                    history.replaceState(null, '', '#');
                } else {
                    history.replaceState(null, '', '#!' + stateString);
                }
            }
        }
    }
    /**
     * Sets the current state to match the URL hash.  If it is desired to initialize the state based
     * on the URL hash, then this should be called immediately after construction.
     */
    updateFromUrlHash() {
        try {
            let s = location.href.replace(/^[^#]+/, '');
            if (s === '' || s === '#' || s === '#!') {
                s = '#!' + this.defaultFragment;
            }
            // Handle remote JSON state
            if (s.match(/^#!([a-z][a-z\d+-.]*):\/\//)) {
                const url = s.substring(2);

                var _parseSpecialUrl = parseSpecialUrl(url, this.credentialsManager);

                const parsedUrl = _parseSpecialUrl.url,
                      credentialsProvider = _parseSpecialUrl.credentialsProvider;

                StatusMessage.forPromise(cancellableFetchSpecialOk(credentialsProvider, parsedUrl, {}, responseJson).then(json => {
                    verifyObject(json);
                    this.root.reset();
                    this.root.restoreState(json);
                }), { initialMessage: `Loading state from ${url}`, errorPrefix: `Error loading state:` });
            } else if (s.startsWith('#!+')) {
                s = s.slice(3);
                // Firefox always %-encodes the URL even if it is not typed that way.
                s = decodeURIComponent(s);
                let state = urlSafeParse(s);
                verifyObject(state);
                this.root.restoreState(state);
                this.prevStateString = undefined;
            } else if (s.startsWith('#!')) {
                s = s.slice(2);
                s = decodeURIComponent(s);
                if (s === this.prevStateString) {
                    return;
                }
                this.prevStateString = s;
                this.root.reset();
                let state = urlSafeParse(s);
                verifyObject(state);
                this.root.restoreState(state);
            } else {
                throw new Error(`URL hash is expected to be of the form "#!{...}" or "#!+{...}".`);
            }
            this.parseError.value = undefined;
        } catch (parseError) {
            this.parseError.value = parseError;
        }
    }
}
//# sourceMappingURL=url_hash_binding.js.map